import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { UI } from '@/lib/Constants'
import { selectOpenCheckSelectModal, setOpenCheckSelectModal, selectBooking } from '@/features/EditBooking/editBookingSlice'
import { fetchChecks, addReservationToCheck } from '@/features/AdvancedPointOfSale/advancedPointOfSaleSlice'
import { setInputLocked } from '@/features/Calendar/calendarSlice'

export default function SelectCheckModal() {

    const dispatch   = useDispatch()
    const open       = useSelector(selectOpenCheckSelectModal)
    const booking    = useSelector(selectBooking)
    const companyId  = booking?.company_id
    const locationId = booking?.location_id

    const [loading, setLoading]                 = useState(false)
    const [checkOptions, setCheckOptions]       = useState([])
    const [selectedCheckId, setSelectedCheckId] = useState(null)

    const handleKeyDown = (e) => {
        if (
            e.key === 'Escape' &&
                !document.body.classList.contains(UI.classes.CONFIRMATION_OPEN) &&
                !document.body.classList.contains(UI.classes.HELPJUICE_OPEN)
        ) {
            dispatch(setOpenCheckSelectModal(false))
        }
    }

    const handleAddToExistingCheck = () => {
        setLoading(true)
        dispatch(setInputLocked(true))

        dispatch(addReservationToCheck(
            booking.id,
            selectedCheckId,
            companyId,
            locationId,
        ))
        .then(({ success }) => {
            if (success) {
                dispatch(setOpenCheckSelectModal(false))
                return
            }
        })
        .finally(() => {
            setLoading(false)
            dispatch(setInputLocked(false))
        })
    }

    // Handle the initial mount and unmount of the component
    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown, false)

        if (open) {
            document.body.classList.add(UI.classes.MULTIPLE_MODALS_OPEN)
        }

        // cleanup/reset on unmount
        return () => {
            document.body.classList.remove(UI.classes.MULTIPLE_MODALS_OPEN)
            document.removeEventListener('keydown', handleKeyDown, false)
        }
    }, [])

    useEffect(() => {
        if (open && !!booking) {
            dispatch(fetchChecks(null, {
                companyId: companyId,
                include_closed: false,
                include_payments: false,
                include_searchable_cards: false,
                exclude_card_on_file: true,
                exclude_check_readiness: true,
                for_checks_grid: true,
            })).then((data) => {
                const checks  = data || []
                const servers = []
                const groups  = []
                const options = [<option key='please-choose' children='Please Choose' value='' />]

                // create a unique group object for each server
                checks.forEach(check => {
                    if (!servers.includes(check?.server?.id)) {
                        servers.push(check?.server?.id)
                        groups.push({ id: check?.server?.id, name: check?.server?.name, checks: [] })
                    }
                })

                // find the server in the groups array and add their checks to the object
                checks.forEach(check => {
                    groups.filter(group => group.id === check?.server?.id)?.[0]?.checks.push(check)
                })

                // sort alpha asc by server name
                groups.sort((a, b) => a.name.localeCompare(b.name))

                groups.forEach(group => {
                    // sort the checks alpha asc by check name
                    group.checks.sort((a, b) => a.name.localeCompare(b.name))

                    // generate each option group with options
                    options.push(
                        <optgroup key={group.name} label={`Server: ${group.name}`}>
                            { group.checks.filter(check => !check.booking).map(({ id, name, check_number }) => (
                                <option key={id} value={id} children={`${name} (${check_number})`} />
                            ))}
                        </optgroup>
                    )
                })

                setCheckOptions(options)
            })
        }
    }, [open, booking])

    return open && (
        <div className="modal modal-backdrop modal--xs">
            <div className="modal-container">
                <div className="modal-header pb-2">
                    <h3>Select a Check</h3>
                </div>
                <div className="modal-body p-4">
                    <div className="container px-1">
                        <select
                            children={checkOptions}
                            className="custom-select"
                            onChange={(e) => setSelectedCheckId(e.target.value)}
                        />

                        <hr className='my-4' />

                        <div className="alert alert-primary rounded" role="alert">
                            <h6 className='alert-heading'>Please Note!</h6>
                            <ul className='pl-3 mb-0'>
                                <li className='my-2'>By assigning this reservation to an existing check, it will update the resource and the name on the existing check to match the name on the reservation.</li>
                                <li className='my-2'>This will disable the ability to take further payments via the Reservation Payment modal.</li>
                                <li className='my-2'>Once sent, payments towards this reservation will need to be made in the RKd Advanced POS.</li>
                            </ul>
                        </div>
                    </div>
                </div>
                <div className="modal-footer">
                    <button
                        children='Cancel'
                        type="button"
                        className="btn btn-outline-primary mr-auto"
                        onClick={() => dispatch(setOpenCheckSelectModal(false))}
                    />

                    <button
                        children={loading ? 'Please Wait...' : 'Send to POS'}
                        type="button"
                        className="btn btn-primary"
                        disabled={loading || !selectedCheckId}
                        onClick={handleAddToExistingCheck}
                    />
                </div>
            </div>
        </div>
    )
}
